import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import SearchFieldStyled from './Search.styled';
import SearchDebounce from './SearchDebounce';
import CloseIcon from '@material-ui/icons/Close';
import { IconButton } from '@material-ui/core';

const SearchField = ({
  field: { name, value, onChange, onBlur },
  form: { errors, touched },
  label,
  required,
  style,
  variant,
  fullWidth,
  disabled,
  inputProps,
  asyncErrors,
  allowClear,
  ...props
}) => {
  // State and setter for search field
  const [searchValue, setSearchValue] = useState(value || '');
  const [search, setSearch] = useState(null);

  const debouncedSearch = SearchDebounce(search, 500);

  useEffect(
    () => {
      // Make sure we have a value (user has entered something in input)
      if (debouncedSearch !== null) {
        onChange(name, debouncedSearch);
      }
    },
    // The useEffect function will only execute if this value changes ...
    // value (search) hasn't changed for more than 500ms.

    // eslint-disable-next-line
    [debouncedSearch]
  );

  useEffect(
    () => {
      // Make sure we have a value (user has entered something in input)
      setSearchValue(value);
    },
    // The useEffect function will only execute if this value changes ...
    // value (search) hasn't changed for more than 500ms.

    // eslint-disable-next-line
    [value]
  );

  const handleShowError = () => {
    if (asyncErrors) {
      return asyncErrors;
    } else if (errors[name] && touched[name] && errors[name]) {
      return errors[name];
    }
  };

  const handleError = () => {
    if (asyncErrors) {
      return true;
    } else if (errors && errors[name] && touched && touched[name]) {
      return true;
    }
  };

  const onClear = () => {
    setSearch('');
    setSearchValue('');
  };

  const getInputProps = () => {
    const data = allowClear
      ? {
          endAdornment: (
            <IconButton className="clearButton" onClick={onClear}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ),
          ...inputProps,
        }
      : {};
    return {
      ...data,
      ...inputProps,
    };
  };

  return (
    <SearchFieldStyled
      name={name}
      style={style}
      type="search"
      label={label}
      margin="normal"
      onBlur={onBlur}
      variant={variant}
      value={searchValue}
      required={required}
      disabled={disabled}
      fullWidth={fullWidth}
      error={handleError()}
      InputProps={{id: name, ...getInputProps()}}
      InputLabelProps={{htmlFor: name}}
      helperText={handleShowError()}
      onChange={(e) => {
        setSearch(e.target.value);
        setSearchValue(e.target.value);
      }}
      {...props}
    />
  );
};

SearchField.propTypes = {
  type: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  inputProps: PropTypes.object,
  style: PropTypes.object,
  label: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

SearchField.defaultProps = {
  type: 'text',
  inputProps: {},
  field: {},
  form: {
    errors: {},
    touched: {},
  },
  required: false,
  style: {},
  fullWidth: true,
  disabled: false,
  label: 'Search...',
  variant: 'standard',
};

export default SearchField;
